import { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { imageUrl } from './api';

export interface Payee {
  workspaceId: string | null;
  bankHolder: string;
  bankName: string;
  bankAccount: string;
  note: string;
  hasWechatQr: boolean;
  hasAlipayQr: boolean;
}

/** An authenticated QR image; `version` forces a reload after it is replaced. */
export function QrImage(props: {
  workspaceId: string;
  kind: 'wechat' | 'alipay';
  version?: number;
}) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = '';
    let live = true;
    setFailed(false);
    imageUrl(`/payment-profiles/${props.workspaceId}/qr/${props.kind}`)
      .then((u) => {
        url = u;
        if (live) setSrc(u);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [props.workspaceId, props.kind, props.version]);
  return (
    <Box
      sx={{
        width: 148,
        height: 148,
        borderRadius: 1,
        border: '1px solid #e7edef',
        bgcolor: '#fff',
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
      }}
    >
      {src ? (
        <img
          src={src}
          alt="收款码"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        <Typography color="text.secondary" sx={{ fontSize: 12 }}>
          {failed ? '加载失败' : '加载中…'}
        </Typography>
      )}
    </Box>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
      <Typography color="text.secondary" sx={{ fontSize: 12.5, width: 56, flex: 'none' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1, wordBreak: 'break-all' }}>
        {value}
      </Typography>
      <Button
        size="small"
        onClick={async () => {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? '已复制' : '复制'}
      </Button>
    </Box>
  );
}

/** Where the supplier wants to be paid, as the buyer sees it. */
export default function PayeeInfo({ payee }: { payee: Payee | null }) {
  const hasBank = !!payee?.bankAccount;
  if (!payee || (!hasBank && !payee.hasWechatQr && !payee.hasAlipayQr))
    return (
      <Typography color="text.secondary" sx={{ fontSize: 13 }}>
        对方尚未设置收款信息，请通过微信或电话向对方确认收款账户。
      </Typography>
    );
  return (
    <Stack spacing={2}>
      {hasBank && (
        <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#f6f8f8' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 650, mb: 0.5 }}>对公转账</Typography>
          {payee.bankHolder && <CopyRow label="户名" value={payee.bankHolder} />}
          {payee.bankName && <CopyRow label="开户行" value={payee.bankName} />}
          <CopyRow label="账号" value={payee.bankAccount} />
        </Box>
      )}
      {(payee.hasWechatQr || payee.hasAlipayQr) && payee.workspaceId && (
        <Stack direction="row" spacing={3}>
          {payee.hasWechatQr && (
            <Box sx={{ textAlign: 'center' }}>
              <QrImage workspaceId={payee.workspaceId} kind="wechat" />
              <Typography sx={{ fontSize: 12.5, mt: 0.75 }}>微信扫码付款</Typography>
            </Box>
          )}
          {payee.hasAlipayQr && (
            <Box sx={{ textAlign: 'center' }}>
              <QrImage workspaceId={payee.workspaceId} kind="alipay" />
              <Typography sx={{ fontSize: 12.5, mt: 0.75 }}>支付宝扫码付款</Typography>
            </Box>
          )}
        </Stack>
      )}
      {payee.note && (
        <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
          对方备注：{payee.note}
        </Typography>
      )}
    </Stack>
  );
}
